import { atom, getDefaultStore } from "jotai";
import { PlaySound } from "~/SoundUtils";

/**
 * 通知ダイアログの表示タイプ
 * success: 成功通知(緑系)
 * error: エラー通知(赤系)
 * info: 情報通知(青系)
 * warning: 警告通知(黄系)
 */
export type NotificationDialogType = "success" | "error" | "info" | "warning";

/**
 * 通知ダイアログの表示モード
 * flash: 一定時間後に自動消滅
 * closable: 閉じるボタンで手動消滅
 */
export type NotificationDialogMode = "flash" | "closable";

/**
 * 通知ダイアログの状態
 */
export interface NotificationDialogState {
  message: string;
  type: NotificationDialogType;
  mode: NotificationDialogMode;
  /** 補足コード (任意): 指定時はメッセージとともに表示される */
  code?: string;
}

// フラッシュモードのデフォルト表示時間(ミリ秒)
const flashDisplayDuration = 5000;

/**
 * 通知ダイアログの表示状態を管理するatom
 * nullの場合は非表示
 */
export const notificationDialogAtom = atom<NotificationDialogState | null>(null);

// フラッシュモードの自動消滅タイマーID
let flashTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * フラッシュタイマーをクリアする
 */
const clearFlashTimer = () => {
  if (flashTimerId !== null) {
    clearTimeout(flashTimerId);
    flashTimerId = null;
  }
};

/**
 * 通知ダイアログを表示する
 * 表示中に呼ばれた場合、既存のダイアログを置き換える
 * @param message 表示するメッセージ
 * @param type 通知タイプ ("info" | "success" | "warning" | "error")
 * @param mode 表示モード (デフォルト: "flash")
 * @param code 補足コード (任意): 指定時はメッセージとともに表示される
 */
export const showNotificationDialog = (
  message: string,
  type: NotificationDialogType,
  mode: NotificationDialogMode = "flash",
  code?: string
): void => {
  const store = getDefaultStore();

  // 既存のフラッシュタイマーをクリア
  clearFlashTimer();

  // ダイアログを表示
  store.set(notificationDialogAtom, {
    message,
    type,
    mode,
    ...(code !== undefined ? { code } : {}),
  });

  // フラッシュモードの場合、自動消滅タイマーを設定
  if (mode === "flash") {
    flashTimerId = setTimeout(() => {
      store.set(notificationDialogAtom, null);
      flashTimerId = null;
    }, flashDisplayDuration);
  }
};

/**
 * 通知ダイアログを閉じる
 */
export const dismissNotificationDialog = (): void => {
  const store = getDefaultStore();
  clearFlashTimer();
  PlaySound("off");
  store.set(notificationDialogAtom, null);
};
